/**
 * RunPod GPU Export Client
 * 
 * Client for submitting video exports to RunPod GPU serverless.
 * Uses NVENC for 10-30x faster encoding than CPU.
 */

// ============================================================================
// TYPES
// ============================================================================

export interface RunPodExportRequest {
    jobId: string;
    projectId: string;
    userId: string;
    clips: any[];
    tracks: any[];
    activeSubtitleLanguage?: string;
    subtitleStyle?: any;
    burnSubtitles?: boolean;
    quality: 'draft' | 'standard' | 'high';
    resolution?: { width: number; height: number };
    outputKey?: string;
}

export interface RunPodExportResult {
    jobId: string;
    status: 'completed' | 'failed';
    downloadUrl?: string;
    encoder: string;
    encoderType: string;
    processingTime: number;
    error?: string;
}

// ============================================================================
// CLIENT
// ============================================================================

export class RunPodExportClient {
    private apiKey: string;
    private endpointId: string;
    private baseUrl: string;

    // R2 credentials for RunPod to upload directly
    private r2Config: {
        endpoint: string;
        accessKeyId: string;
        secretAccessKey: string;
        bucket: string;
        publicUrl: string;
    };

    constructor(config: {
        apiKey: string;
        endpointId: string;
        customUrl?: string; // Add this
        r2Endpoint: string;
        r2AccessKeyId: string;
        r2SecretAccessKey: string;
        r2Bucket: string;
        r2PublicUrl: string;
    }) {
        this.apiKey = config.apiKey;
        this.endpointId = config.endpointId;
        // Use custom URL if provided (for dedicated Pods), otherwise standard Serverless URL
        this.baseUrl = config.customUrl
            ? config.customUrl.replace(/\/$/, '')
            : `https://api.runpod.ai/v2/${config.endpointId}`;

        this.r2Config = {
            endpoint: config.r2Endpoint,
            accessKeyId: config.r2AccessKeyId,
            secretAccessKey: config.r2SecretAccessKey,
            bucket: config.r2Bucket,
            publicUrl: config.r2PublicUrl,
        };
    }

    private assertDirectApiConfigured(): void {
        if (!this.apiKey) {
            throw new Error('RunPod direct API key is not configured. Use the Firebase proxy flow instead.');
        }
    }

    /**
     * Submit a GPU export job (async - returns immediately)
     */
    async submitExport(request: RunPodExportRequest): Promise<{ id: string }> {
        this.assertDirectApiConfigured();

        const response = await fetch(`${this.baseUrl}/run`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${this.apiKey}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                input: {
                    ...request,
                    // Pass R2 credentials for direct upload
                    r2Endpoint: this.r2Config.endpoint,
                    r2AccessKeyId: this.r2Config.accessKeyId,
                    r2SecretAccessKey: this.r2Config.secretAccessKey,
                    r2Bucket: this.r2Config.bucket,
                    r2PublicUrl: this.r2Config.publicUrl,
                },
            }),
        });

        if (!response.ok) {
            const error = await response.text();
            throw new Error(`RunPod API error: ${error}`);
        }

        const data = await response.json();
        console.log(`[RunPod] Job submitted: ${data.id}`);
        return { id: data.id };
    }

    /**
     * Submit and wait for completion (sync - blocks until done)
     */
    async submitExportSync(request: RunPodExportRequest): Promise<RunPodExportResult> {
        console.log('[RunPodClient] Calling GPU via Proxy (CORS bypass)...');

        try {
            // Import dynamically to avoid circular deps if needed, or use the imported firebase functions
            // Assuming 'functions' is globally available or imported.
            // Let's use the native fetch to our OWN backend proxy if callable is tricky, 
            // but callable is best.
            // We need to import functions from ./services/firebase-api in the file header first.
            // For now, let's use the global firebase object if available or assume 'functions' import.

            const { functions } = await import('./services/firebase-api'); // Dynamic import to be safe

            const proxyRunPod = functions.httpsCallable('proxyRunPod');

            const result = await proxyRunPod({
                input: {
                    ...request,
                    // R2 creds are injected server-side by the proxy for security!
                    // But we can send them if we want to override.
                    // Let the proxy handle secrets.
                }
            });

            const data = result.data as any; // Type assertion

            if (data.status === 'COMPLETED') {
                return data.output as RunPodExportResult;
            }

            throw new Error(`RunPod job failed: ${data.error || 'Unknown error'}`);

        } catch (err: any) {
            console.error('[RunPodClient] Proxy Error:', err);
            throw new Error(`GPU Proxy Failed: ${err.message}`);
        }
    }

    /**
     * Check job status
     */
    async getJobStatus(jobId: string): Promise<{
        status: 'IN_QUEUE' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
        output?: RunPodExportResult;
    }> {
        this.assertDirectApiConfigured();

        const response = await fetch(`${this.baseUrl}/status/${jobId}`, {
            headers: {
                'Authorization': `Bearer ${this.apiKey}`,
            },
        });

        if (!response.ok) {
            const error = await response.text();
            throw new Error(`RunPod API error: ${error}`);
        }

        return response.json();
    }

    /**
     * Poll until job completes
     */
    async waitForCompletion(
        jobId: string,
        onProgress?: (status: string) => void,
        pollInterval = 2000
    ): Promise<RunPodExportResult> {
        while (true) {
            const status = await this.getJobStatus(jobId);

            onProgress?.(status.status);

            if (status.status === 'COMPLETED' && status.output) {
                return status.output;
            }

            if (status.status === 'FAILED') {
                throw new Error('RunPod job failed');
            }

            await new Promise(resolve => setTimeout(resolve, pollInterval));
        }
    }

    /**
     * Cancel a running job
     */
    async cancelJob(jobId: string): Promise<void> {
        this.assertDirectApiConfigured();

        await fetch(`${this.baseUrl}/cancel/${jobId}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${this.apiKey}`,
            },
        });
    }
}

// ============================================================================
// FACTORY
// ============================================================================

/**
 * Create RunPod client from environment variables
 */
export function createRunPodClient(): RunPodExportClient | null {
    const endpointId = import.meta.env.VITE_RUNPOD_ENDPOINT_ID;
    const customUrl = import.meta.env.VITE_RUNPOD_CUSTOM_URL;

    return new RunPodExportClient({
        // Frontend exports use the Firebase proxy, so no RunPod secret should live in the browser bundle.
        apiKey: '',
        endpointId: endpointId || 'manual',
        customUrl,
        r2Endpoint: import.meta.env.VITE_R2_ENDPOINT || '',
        r2AccessKeyId: import.meta.env.VITE_R2_ACCESS_KEY_ID || '',
        r2SecretAccessKey: import.meta.env.VITE_R2_SECRET_ACCESS_KEY || '',
        r2Bucket: import.meta.env.VITE_R2_BUCKET || '',
        r2PublicUrl: import.meta.env.VITE_R2_PUBLIC_URL || '',
    });
}
